/**
 * The agenda. On a phone this is what people actually read — the month grid is for
 * orientation, this is for "what's happening". Grouped by day, each row carries the
 * owner's name in text as well as their color, so the color is decoration, never data.
 */
import type { CalendarEvent, MemberSummary } from '../../shared/types';
import {
  addDays,
  agendaDayKey,
  dayKey,
  eventDayKeys,
  formatDayLabel,
  formatEventTime,
  localDate,
  memberOf,
  todayKey,
} from './lib';

/** True when formatDayLabel() would say "Today"/"Tomorrow" instead of an actual date. */
function isRelativeLabel(key: string): boolean {
  return key === todayKey() || key === dayKey(addDays(new Date(), 1));
}

interface Props {
  events: CalendarEvent[]; // already in the order to display
  members: MemberSummary[];
  /** Anchor day for multi-day events — set when showing one selected day. */
  dayScope?: string | null;
  emptyTitle: string;
  emptyBody: string;
  emptyAction?: React.ReactNode;
  onEdit?: (e: CalendarEvent) => void;
  onDelete?: (e: CalendarEvent) => void;
}

export default function AgendaList({
  events,
  members,
  dayScope = null,
  emptyTitle,
  emptyBody,
  emptyAction,
  onEdit,
  onDelete,
}: Props) {
  if (events.length === 0) {
    return (
      <div className="rounded-card border border-dashed border-border bg-surface px-5 py-8 text-center">
        <p className="font-display text-base font-semibold text-ink">{emptyTitle}</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-ink-soft">{emptyBody}</p>
        {emptyAction && <div className="mt-4 flex justify-center">{emptyAction}</div>}
      </div>
    );
  }

  // One heading per day. When a single day is in scope we skip the grouping headers.
  // The grouping key must match the sort key in sortAgenda(), or groups would repeat.
  const groups: { key: string; events: CalendarEvent[] }[] = [];
  if (dayScope) {
    groups.push({ key: dayScope, events });
  } else {
    for (const ev of events) {
      const key = agendaDayKey(ev);
      const last = groups[groups.length - 1];
      if (last && last.key === key) last.events.push(ev);
      else groups.push({ key, events: [ev] });
    }
  }

  return (
    <ol className="space-y-5">
      {groups.map((group) => (
        <li key={group.key}>
          <h3 className="mb-2 flex items-baseline gap-2 text-sm font-semibold text-ink">
            {formatDayLabel(group.key)}
            {/* "Today"/"Tomorrow" hide the date, so spell it out. Otherwise the label
                already is the date and repeating it is noise. */}
            {isRelativeLabel(group.key) && (
              <span className="text-xs font-normal text-ink-faint">
                {localDate(group.key).toLocaleDateString(undefined, {
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            )}
          </h3>
          <ul className="space-y-2">
            {group.events.map((ev) => (
              <EventRow
                key={`${group.key}-${ev.id}`}
                event={ev}
                members={members}
                day={group.key}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </ul>
        </li>
      ))}
    </ol>
  );
}

function EventRow({
  event,
  members,
  day,
  onEdit,
  onDelete,
}: {
  event: CalendarEvent;
  members: MemberSummary[];
  day: string;
  onEdit?: (e: CalendarEvent) => void;
  onDelete?: (e: CalendarEvent) => void;
}) {
  const owner = memberOf(event, members);
  const keys = eventDayKeys(event);
  const isContinuation = keys.length > 1 && keys[0] !== day;
  const spansDays = keys.length > 1;

  const body = (
    <div className="min-w-0 flex-1">
      <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="font-medium text-ink">{event.title}</span>
        {spansDays && (
          <span className="text-xs text-ink-faint">
            {isContinuation ? 'continues' : `${keys.length} days`}
          </span>
        )}
      </p>
      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-ink-soft">
        <span className="tabular-nums">{formatEventTime(event)}</span>
        <span aria-hidden="true" className="text-ink-faint">
          ·
        </span>
        <span className="inline-flex items-center gap-1">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: owner.color }}
            aria-hidden="true"
          />
          {owner.name}
        </span>
        {event.location && (
          <>
            <span aria-hidden="true" className="text-ink-faint">
              ·
            </span>
            <span className="truncate">{event.location}</span>
          </>
        )}
      </p>
    </div>
  );

  // Native events (event.editable) can be changed here; the Google mirror is read-only.
  const controls =
    event.editable && (onEdit || onDelete) ? (
      <div className="flex shrink-0 items-start gap-1">
        {onEdit && (
          <button
            type="button"
            onClick={() => onEdit(event)}
            className="rounded-full px-2 py-1 text-xs font-medium text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Edit
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={() => onDelete(event)}
            className="rounded-full px-2 py-1 text-xs font-medium text-clay hover:bg-clay-soft focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay"
          >
            Delete
          </button>
        )}
      </div>
    ) : null;

  return (
    <li
      className="flex gap-3 rounded-card border border-border bg-surface p-3"
      style={{ borderLeft: `4px solid ${owner.color}` }}
    >
      {event.description ? (
        <details className="min-w-0 flex-1 [&_summary::-webkit-details-marker]:hidden">
          <summary className="flex min-h-[2.75rem] cursor-pointer list-none items-center gap-2 rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage">
            {body}
            <span className="shrink-0 text-xs text-ink-faint">Details</span>
          </summary>
          <p className="mt-2 border-t border-border pt-2 text-sm whitespace-pre-line text-ink-soft">
            {event.description}
          </p>
        </details>
      ) : (
        <div className="flex min-h-[2.75rem] min-w-0 flex-1 items-center">{body}</div>
      )}
      {controls}
    </li>
  );
}

/** Shared by the page header and the empty states so the colors read consistently. */
export function MemberLegend({ members }: { members: MemberSummary[] }) {
  if (members.length === 0) return null;
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-soft">
      {members.map((m) => (
        <li key={m.id} className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: m.color }}
            aria-hidden="true"
          />
          {m.name}
        </li>
      ))}
      <li className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-ink-soft" aria-hidden="true" />
        Family
      </li>
    </ul>
  );
}

/** Exported for the day-scoped list: filter to one day, keep the display order. */
export function eventsOnDay(events: CalendarEvent[], key: string): CalendarEvent[] {
  return events.filter((ev) => eventDayKeys(ev).includes(key));
}
